import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { PlatformProfile } from "@ti/core";
import { ArrowRight, BadgeCheck, ExternalLink } from "lucide-react";
import { useEffect } from "react";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/misc";
import { buttonVariants } from "@/components/ui/button";
import { PlatformBadge } from "@/features/platform-profile/badge";
import { ArsTrendChart, InsuredAgentsChart, TierDistributionChart } from "@/features/platform-profile/charts";
import { DisciplineKpis, HowMeasured, ScaleKpis, TopScenarios } from "@/features/platform-profile/sections";
import { FooterSeal, PublicError, PublicFrame, PublicSection, useLightTheme, Wordmark } from "@/features/protect/public-layout";
import { q } from "@/lib/api";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/insured/$platformSlug")({
  component: PlatformProfilePage,
});

const WIDTH = "max-w-6xl";

function PlatformProfilePage() {
  const { platformSlug } = Route.useParams();
  const query = useQuery(q.platformProfile(platformSlug));
  useLightTheme();

  const p = query.data;
  useEffect(() => {
    document.title = p ? `${p.platform.name} · Insured with traces.insure` : "Insured with traces.insure";
  }, [p]);

  return (
    <PublicFrame
      width={WIDTH}
      header={
        <>
          <a href="https://traces.insure" className="text-[15px] font-semibold tracking-tight">
            <Wordmark />
          </a>
          <span className="text-xs text-fg-3">Public platform profile</span>
        </>
      }
      footer={
        <>
          <span>Aggregated, anonymized telemetry across insured customers. traces.insure administers coverage and evidence; insurers decide claims.</span>
          <FooterSeal label="Data by" />
        </>
      }
    >
      {query.error ? (
        <PublicError
          error={query.error}
          retry={() => void query.refetch()}
          notFoundTitle="We couldn't find this platform"
          notFoundBody="There's no public profile at this address. The link may be mistyped, or the platform may not have agents insured with traces.insure yet."
          errorTitle="We couldn't load this profile"
        />
      ) : p ? (
        <Profile p={p} />
      ) : (
        <ProfileSkeleton />
      )}
    </PublicFrame>
  );
}

const initials = (name: string) =>
  name
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const fmtSince = (iso: string) => new Date(iso).toLocaleString("en-US", { month: "long", year: "numeric" });
const host = (url: string) => url.replace(/^https?:\/\//, "").replace(/\/$/, "");

function Profile({ p }: { p: PlatformProfile }) {
  const name = p.platform.name;
  return (
    <div className="flex flex-col gap-12">
      <section aria-labelledby="platform-title" className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <span className="grid size-14 shrink-0 place-items-center rounded-2xl bg-fg text-lg font-bold tracking-tight text-background" aria-hidden>
            {initials(name)}
          </span>
          <div className="min-w-0">
            <h1 id="platform-title" className="text-[28px] font-semibold leading-tight tracking-tight sm:text-4xl">
              {name}
            </h1>
            <p className="mt-1.5 flex flex-col gap-x-3 gap-y-0.5 text-sm text-fg-2 sm:flex-row sm:flex-wrap sm:items-center">
              <a href={p.platform.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-accent underline-offset-4 hover:underline">
                {host(p.platform.website)}
                <ExternalLink className="size-3.5" aria-hidden />
              </a>
              <span className="hidden text-fg-3 sm:inline" aria-hidden>
                ·
              </span>
              <span>Member since {fmtSince(p.since)}</span>
            </p>
            <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-fg-2">
              How agents built on {name} perform in production, measured continuously across {p.insuredCustomers} insured {p.insuredCustomers === 1 ? "customer" : "customers"}.
            </p>
          </div>
        </div>
        <Seal />
      </section>

      <PublicSection id="scale" title="Insured at scale">
        <ScaleKpis p={p} />
      </PublicSection>

      <PublicSection id="discipline" title="Operational discipline" description="How completely agents are observed, controlled and fixed when something goes wrong.">
        <DisciplineKpis p={p} />
      </PublicSection>

      <section aria-label="Risk scores" className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader title="Agents by ARS tier" description="Agent Reliability Score tier, all monitored agents" />
          <CardBody>
            <TierDistributionChart data={p.tierDistribution} />
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="6-month trend" description="Monthly, across all monitored agents" />
          <CardBody className="flex flex-col gap-4">
            <div>
              <h4 className="mb-1 text-xs font-medium text-fg-2">Average ARS</h4>
              <ArsTrendChart data={p.monthly} />
            </div>
            <div className="border-t pt-3">
              <h4 className="mb-1 text-xs font-medium text-fg-2">Insured agents</h4>
              <InsuredAgentsChart data={p.monthly} />
            </div>
          </CardBody>
        </Card>
      </section>

      <section aria-label="Scenarios and methodology" className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader title="Most insured scenarios" description="What customers insure their agents for, by number of policies" />
          <CardBody>
            <TopScenarios items={p.topScenarios} />
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="How this is measured" />
          <CardBody>
            <HowMeasured platform={name} />
          </CardBody>
        </Card>
      </section>

      <PublicSection id="badge" title={`Show that ${name} is insured`} description="Embed the badge on your site. It links back to this live profile.">
        <PlatformBadge slug={p.platform.slug} name={name} />
      </PublicSection>

      <section aria-labelledby="cta-title" className="flex flex-col items-start gap-4 rounded-2xl border bg-surface px-6 py-7 sm:flex-row sm:items-center sm:justify-between sm:px-8">
        <div>
          <h2 id="cta-title" className="text-lg font-semibold tracking-tight">
            Building on {name}? Connect your agents
          </h2>
          <p className="mt-1 max-w-xl text-sm text-fg-2">Get a live risk score and insurance for the agents you build — set up takes minutes with the built-in integration.</p>
        </div>
        <Link to="/setup" className={cn(buttonVariants({ size: "lg" }), "h-10 shrink-0 px-5 text-sm")}>
          Connect your agents <ArrowRight />
        </Link>
      </section>
    </div>
  );
}

function Seal() {
  return (
    <div className="flex shrink-0 items-center gap-3 self-start rounded-2xl border border-accent/30 bg-accent-soft/50 px-4 py-3 lg:self-auto">
      <BadgeCheck className="size-8 text-accent" aria-hidden />
      <div>
        <p className="text-sm font-semibold text-fg">Insured with traces.insure</p>
        <p className="text-xs text-fg-2">Verified from live telemetry</p>
      </div>
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <div className="flex flex-col gap-8" aria-busy="true" aria-label="Loading platform profile">
      <div className="flex items-center gap-4">
        <Skeleton className="size-14 rounded-2xl" />
        <div className="flex flex-col gap-2">
          <Skeleton className="h-9 w-56" />
          <Skeleton className="h-4 w-72" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {Array.from({ length: 10 }, (_, i) => (
          <Skeleton key={i} className="h-24 rounded-lg" />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Skeleton className="h-72 rounded-lg" />
        <Skeleton className="h-72 rounded-lg" />
      </div>
    </div>
  );
}
